import _ from 'lodash';
import moment from 'moment';
import pluralize from 'pluralize';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  DateTime,
  ExportDropdown,
  HelpTooltip,
  Hours,
  Inline,
  Percent,
  PeriodFilter,
  Tooltip,
  YesNoFilter,
} from '~/components';
import ClientFiltersBar from '~/components/filters/ClientFiltersBar';
import ClientFiltersGroup from '~/components/filters/ClientFiltersGroup';
import FilterButton from '~/components/filters/FilterButton';
import MemberFiltersBar from '~/components/filters/MemberFiltersBar';
import MemberFiltersGroup from '~/components/filters/MemberFiltersGroup';
import ProjectFiltersBar from '~/components/filters/ProjectFiltersBar';
import ProjectFiltersGroup from '~/components/filters/ProjectFiltersGroup';
import SingleSelectFilter from '~/components/filters/SingleSelectFilter';
import { useApi, useConfirmation, useWorkspace } from '~/contexts';
import { useDocumentTitle, useSearchParams, useSearchParamsConfig } from '~/hooks';
import useClientFilters from '~/hooks/useClientFilters';
import useMemberFilters from '~/hooks/useMemberFilters';
import useProjectFilters from '~/hooks/useProjectFilters';
import projectStatuses from '~/lookups/project-statuses.js';
import { PageLoader } from '~/routes/public/pages';
import { colors, weights } from '~/styles';
import { dateFormats, mimeTypes } from '~/utils';
import ExportDialogAsync from '../components/ExportDialogAsync';
import Report from '../components/Report';
import Table from '../components/table';
import ReportActions from '../ReportActions';
import { intervals, intervalsByScope } from './intervals';

export default function UtilizationPlanByMember({ report }) {
  useDocumentTitle(report.name);
  const { workspace } = useWorkspace();
  const api = useApi();

  const searchParamsConfig = useSearchParamsConfig();

  const [query, setQuery] = useState({ report: null, status: 'loading' });

  const clientFilters = useClientFilters();
  const projectFilters = useProjectFilters(() => ({
    projectRecordStatusId: {
      ...searchParamsConfig.projectRecordStatusId,
      default: 'active',
    },
    projectStatuses: {
      ...searchParamsConfig.projectStatuses,
      default: [projectStatuses.not_started, projectStatuses.in_progress],
    },
  }));
  const memberFilters = useMemberFilters({
    memberStatusId: { ...searchParamsConfig.memberStatusId, default: 'active' },
  });

  const [params, setParams] = useState({
    period: null,
    unit: '',

    metric: 'billable',

    utilizationMode: 'default',

    ...memberFilters.filters,
    ...clientFilters.filters,
    ...projectFilters.filters,
  });

  const [searchParamsStatus, setSearchParamsStatus] = useState('pending');
  const searchParams = useSearchParams({
    config: useMemo(
      () => ({
        period: {
          ...searchParamsConfig.periodByUnit,
          default: intervals.next_6_months,
          intervals: (unit) => intervalsByScope[unit],
          defaults: {
            day: intervals.next_30_days,
            week: intervals.next_12_weeks,
            month: intervals.next_6_months,
          },
        },
        unit: { default: 'month', valid: ['day', 'week', 'month'] },

        ...memberFilters.searchParamsConfig,
        ...clientFilters.searchParamsConfig,
        ...projectFilters.searchParamsConfig,

        metric: { default: 'billable', valid: ['billable', 'client', 'productive', 'total'] },

        utilizationMode: {
          valid: ['default', 'time_off_adjusted'],
          default: workspace.defaultAdjustForTimeOff ? 'time_off_adjusted' : 'default',
        },
      }),
      [searchParamsConfig, clientFilters, projectFilters, memberFilters, workspace.defaultAdjustForTimeOff],
    ),
    onChange: (params) => setParams((state) => ({ ...state, ...params })),
  });

  // Map the values to perform the API query
  const urlSearchParams = useMemo(
    () => ({
      start: params.period?.start ?? undefined,
      end: params.period?.end ?? undefined,
      unit: params.unit,

      ...memberFilters.mapUrlSearchParams(params),
      ...clientFilters.mapUrlSearchParams(params),
      ...projectFilters.mapUrlSearchParams(params),
    }),
    [params, clientFilters, projectFilters, memberFilters],
  );

  useEffect(() => {
    if (searchParamsStatus === 'ready') return;
    searchParams.get().then((params) => {
      setParams((state) => ({ ...state, ...params }));
      setSearchParamsStatus('ready');
    });
  }, [searchParams, searchParamsStatus]);

  const fetchData = useCallback(async () => {
    const { data } = await api.www.workspaces(workspace.id).reports(report.id).data(urlSearchParams);
    setQuery((state) => ({ ...state, data, status: 'ready' }));
  }, [api, workspace.id, report.id, urlSearchParams]);

  useEffect(() => {
    if (searchParamsStatus !== 'ready') return;
    fetchData();
  }, [fetchData, searchParamsStatus]);

  const [filtersVisible, setFiltersVisible] = useState(false);
  const showFilters = () => setFiltersVisible(true);
  const hideFilters = () => setFiltersVisible(false);
  const handleApplyFilters = (values) => {
    if (values !== params) setQuery((state) => ({ ...state, status: 'filtering' }));

    setParams({ ...params, ...values });
    searchParams.set(values);
    hideFilters();
  };

  const handleMetricChange = (metric) => {
    setParams({ ...params, metric });
    searchParams.set({ metric });
  };

  const handleUtilizationModeChange = (utilizationMode) => {
    setParams({ ...params, utilizationMode });
    searchParams.set({ utilizationMode });
  };

  const confirmation = useConfirmation();

  const handleExport = (mimeType) => {
    confirmation.prompt((resolve) => (
      <ExportDialogAsync
        onLoad={api.www
          .workspaces(workspace.id)
          .reports(report.id)
          .data(urlSearchParams, {
            headers: { accept: mimeType },
          })}
        onClose={resolve}
      />
    ));
  };

  return (
    <Report>
      <Report.Header>
        <Report.Info report={report} />

        <Report.Actions>
          <ReportActions onSave={() => searchParams.set(params)} />
          <ExportDropdown>
            {({ setIsOpen }) => (
              <>
                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport(mimeTypes.csv);
                    setIsOpen(false);
                  }}>
                  Export to CSV
                </ExportDropdown.Item>

                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport(mimeTypes.xlsx);
                    setIsOpen(false);
                  }}>
                  Export to Excel
                </ExportDropdown.Item>
              </>
            )}
          </ExportDropdown>

          <FilterButton isOutline onClick={showFilters} />
        </Report.Actions>
      </Report.Header>

      <Report.FiltersBar>
        <PeriodFilter
          clearable={false}
          scope={params.unit}
          intervals={intervalsByScope[params.unit]}
          placeholder="Date Range"
          value={params.period}
          onChange={({ target: { value } }) => handleApplyFilters({ period: value })}
        />

        <UnitFilter
          value={params.unit}
          onChange={({ target: { value } }) =>
            handleApplyFilters({
              unit: value,
              period: {
                day: intervals.next_30_days,
                week: intervals.next_12_weeks,
                month: intervals.next_6_months,
              }[value],
            })
          }
        />

        <Inline>
          <YesNoFilter
            icon="gear"
            placeholder="Adjust for Paid Time Off"
            value={params.utilizationMode === 'time_off_adjusted' ? 'yes' : 'no'}
            onChange={({ target: { value } }) =>
              handleUtilizationModeChange(value === 'yes' ? 'time_off_adjusted' : 'default')
            }
          />
          <HelpTooltip
            style={{ marginLeft: '.5rem' }}
            message="Reduces the capacity by the number of Time Off hours taken during the period."
          />
        </Inline>

        <MetricFilter value={params.metric} onChange={({ value }) => handleMetricChange(value)} />

        <ClientFiltersBar filters={params} onChange={handleApplyFilters} />
        <ProjectFiltersBar filters={params} onChange={handleApplyFilters} />
        <MemberFiltersBar filters={params} onChange={handleApplyFilters} />
      </Report.FiltersBar>

      {(() => {
        switch (query.status) {
          case 'loading':
          case 'filtering':
            return <PageLoader />;

          default:
            return (
              <>
                <Data query={query} params={params} />
                <Filters values={params} isOpen={filtersVisible} onApply={handleApplyFilters} onClose={hideFilters} />
              </>
            );
        }
      })()}
    </Report>
  );
}

function Data({ query, params }) {
  const report = query.data;

  const { members, records, periods } = useMemo(() => {
    const members = _.orderBy(report.members, (r) => r.name?.toLowerCase());

    const records = report.records.reduce(
      (a, v) => {
        const capacity = {
          default: v.capacity,
          time_off_adjusted: v.capacityTimeOffAdjusted,
        }[params.utilizationMode];

        a.total.capacity += capacity || 0;
        a.total.billableHours += v.billableHours || 0;
        a.total.timeOffHours += v.timeOffHours || 0;
        a.total.totalClientHours += v.totalClientHours || 0;
        a.total.totalProductiveHours += v.totalProductiveHours || 0;
        a.total.totalProjectHours += v.totalProjectHours || 0;
        a.total.totalHours += v.totalHours || 0;

        // Members
        a.members[v.memberId] = a.members[v.memberId] || {
          total: {
            capacity: 0,
            billableHours: 0,
            timeOffHours: 0,
            totalClientHours: 0,
            totalProductiveHours: 0,
            totalProjectHours: 0,
            totalHours: 0,
            get billableUtilization() {
              if (!this.capacity) return null;
              return this.billableHours / this.capacity;
            },
            get clientUtilization() {
              if (!this.capacity) return null;
              return this.totalClientHours / this.capacity;
            },
            get productiveUtilization() {
              if (!this.capacity) return null;
              return this.totalProductiveHours / this.capacity;
            },
            get totalUtilization() {
              if (!this.capacity) return null;
              return this.totalProjectHours / this.capacity;
            },
          },
          periods: {},
        };
        a.members[v.memberId].total.capacity += capacity || 0;
        a.members[v.memberId].total.billableHours += v.billableHours || 0;
        a.members[v.memberId].total.timeOffHours += v.timeOffHours || 0;
        a.members[v.memberId].total.totalClientHours += v.totalClientHours || 0;
        a.members[v.memberId].total.totalProductiveHours += v.totalProductiveHours || 0;
        a.members[v.memberId].total.totalProjectHours += v.totalProjectHours || 0;
        a.members[v.memberId].total.totalHours += v.totalHours || 0;
        if (!a.members[v.memberId].periods[v.start])
          a.members[v.memberId].periods[v.start] = {
            capacity: 0,
            billableHours: 0,
            timeOffHours: 0,
            totalClientHours: 0,
            totalProductiveHours: 0,
            totalProjectHours: 0,
            totalHours: 0,
            get billableUtilization() {
              if (!this.capacity) return null;
              return this.billableHours / this.capacity;
            },
            get clientUtilization() {
              if (!this.capacity) return null;
              return this.totalClientHours / this.capacity;
            },
            get productiveUtilization() {
              if (!this.capacity) return null;
              return this.totalProductiveHours / this.capacity;
            },
            get totalUtilization() {
              if (!this.capacity) return null;
              return this.totalProjectHours / this.capacity;
            },
          };
        a.members[v.memberId].periods[v.start].capacity += capacity || 0;
        a.members[v.memberId].periods[v.start].billableHours += v.billableHours || 0;
        a.members[v.memberId].periods[v.start].timeOffHours += v.timeOffHours || 0;
        a.members[v.memberId].periods[v.start].totalClientHours += v.totalClientHours || 0;
        a.members[v.memberId].periods[v.start].totalProductiveHours += v.totalProductiveHours || 0;
        a.members[v.memberId].periods[v.start].totalProjectHours += v.totalProjectHours || 0;
        a.members[v.memberId].periods[v.start].totalHours += v.totalHours || 0;

        // Periods
        if (!a.periods[v.start])
          a.periods[v.start] = {
            capacity: 0,
            billableHours: 0,
            timeOffHours: 0,
            totalClientHours: 0,
            totalProductiveHours: 0,
            totalProjectHours: 0,
            totalHours: 0,
            get billableUtilization() {
              if (!this.capacity) return null;
              return this.billableHours / this.capacity;
            },
            get clientUtilization() {
              if (!this.capacity) return null;
              return this.totalClientHours / this.capacity;
            },
            get productiveUtilization() {
              if (!this.capacity) return null;
              return this.totalProductiveHours / this.capacity;
            },
            get totalUtilization() {
              if (!this.capacity) return null;
              return this.totalProjectHours / this.capacity;
            },
          };
        a.periods[v.start].capacity += capacity || 0;
        a.periods[v.start].billableHours += v.billableHours || 0;
        a.periods[v.start].timeOffHours += v.timeOffHours || 0;
        a.periods[v.start].totalClientHours += v.totalClientHours || 0;
        a.periods[v.start].totalProductiveHours += v.totalProductiveHours || 0;
        a.periods[v.start].totalProjectHours += v.totalProjectHours || 0;
        a.periods[v.start].totalHours += v.totalHours || 0;

        return a;
      },
      {
        members: {},
        periods: {},
        total: {
          capacity: 0,
          billableHours: 0,
          timeOffHours: 0,
          totalClientHours: 0,
          totalProductiveHours: 0,
          totalProjectHours: 0,
          totalHours: 0,
          get billableUtilization() {
            if (!this.capacity) return null;
            return this.billableHours / this.capacity;
          },
          get clientUtilization() {
            if (!this.capacity) return null;
            return this.totalClientHours / this.capacity;
          },
          get productiveUtilization() {
            if (!this.capacity) return null;
            return this.totalProductiveHours / this.capacity;
          },
          get totalUtilization() {
            if (!this.capacity) return null;
            return this.totalProjectHours / this.capacity;
          },
        },
      },
    );

    let start = params.period.start;
    let end = params.period.end;

    switch (params.unit) {
      case 'day':
        start = moment(start).format(dateFormats.isoDate);
        end = moment(end).format(dateFormats.isoDate);
        break;

      case 'week':
        start = moment(start).startOf('isoWeek').format(dateFormats.isoDate);
        end = moment(end).endOf('isoWeek').format(dateFormats.isoDate);
        break;

      case 'month':
        start = moment(start).startOf('month').format(dateFormats.isoDate);
        end = moment(end).endOf('month').format(dateFormats.isoDate);
        break;
    }

    const periodCount = moment(end).diff(start, params.unit) + 1;
    const periods = [];
    for (let index = 0; index < periodCount; index++) {
      periods.push(moment(start).add(index, params.unit).format(dateFormats.isoDate));
    }

    return { members, records, periods };
  }, [report, params.period.start, params.period.end, params.unit, params.utilizationMode]);

  const { metric } = params;

  return (
    <Report.Table>
      <Table>
        <Table.Header>
          <Table.Column sticky minWidth="16rem">
            Member
          </Table.Column>

          {periods.map((p) => (
            <Table.Column key={p} align="right" width="7rem">
              <DateTime value={p} />
            </Table.Column>
          ))}

          <Table.Column align="right" width="7rem">
            Total
          </Table.Column>
        </Table.Header>

        <Table.Body>
          {members.map((member) => {
            return (
              <Table.Row key={member.id}>
                <Table.Cell>{member.name}</Table.Cell>

                {periods.map((period) => {
                  const value = records.members[member.id]?.periods[period];

                  return (
                    <Table.Cell key={period}>
                      <CellValue value={value} metric={metric} />
                    </Table.Cell>
                  );
                })}

                <Table.Cell>
                  <CellValue value={records.members[member.id]?.total} metric={metric} />
                </Table.Cell>
              </Table.Row>
            );
          })}

          <Table.Row style={{ fontWeight: weights.bold }}>
            <Table.Cell>Total</Table.Cell>

            {periods.map((period) => {
              const value = records.periods[period];

              return (
                <Table.Cell key={period}>
                  <CellValue value={value} metric={metric} />
                </Table.Cell>
              );
            })}

            <Table.Cell>
              <CellValue value={records.total} metric={metric} />
            </Table.Cell>
          </Table.Row>
        </Table.Body>

        <Table.Status total={members.length} label="Member" isLoading={query.status !== 'ready'} />
      </Table>
    </Report.Table>
  );
}

function Filters({ values, isOpen, onClose, onApply }) {
  const [filters, setFilters] = useState(values);

  const handleApply = () => {
    onApply(filters);
  };

  const handleFilter = (filter) => {
    setFilters({ ...filters, ...filter });
  };

  const handleCancel = () => {
    setFilters(values);
    onClose();
  };

  useEffect(() => {
    setFilters(values);
  }, [values]);

  return (
    <Report.FiltersDrawer isOpen={isOpen} onApply={handleApply} onClose={handleCancel}>
      <UnitFilter
        value={filters.unit}
        onChange={({ target: { value } }) =>
          handleFilter({
            unit: value,
            period: {
              day: intervals.next_30_days,
              week: intervals.next_12_weeks,
              month: intervals.next_6_months,
            }[value],
          })
        }
      />

      <PeriodFilter
        clearable={false}
        scope={filters.unit}
        intervals={intervalsByScope[filters.unit]}
        placeholder="Date Range"
        value={filters.period}
        onChange={({ target: { value } }) => handleFilter({ period: value })}
      />

      <ClientFiltersGroup filters={filters} onChange={handleFilter} />

      <ProjectFiltersGroup filters={filters} onChange={handleFilter} />

      <MemberFiltersGroup filters={filters} onChange={handleFilter} />
    </Report.FiltersDrawer>
  );
}

const TooltipContainer = styled.div`
  min-width: 14rem;
  padding: 0.75rem 0;
`;

const TooltipInfo = styled.div`
  display: flex;
  font-size: 0.75rem;

  &:not(:last-child) {
    margin-bottom: 0.75rem;
  }
`;

const TooltipLabel = styled.div`
  width: 11.5rem;
  padding-right: 0.5rem;
  color: ${colors.grey40};
  font-weight: ${weights.black};
  letter-spacing: 0.0625rem;
  text-transform: uppercase;
  flex-shrink: 0;
  white-space: wrap;
`;

const TooltipValue = styled.div`
  white-space: wrap;
`;

function TooltipHours({ value }) {
  return (
    <>
      <Hours value={value} /> {_.isNumber(value) && pluralize('hours', value)}
    </>
  );
}

function CellValue({ value, metric }) {
  if (!value) return <Percent value={null} />;

  return (
    <Tooltip
      maxWidth="25rem"
      message={
        <TooltipContainer>
          <TooltipInfo>
            <TooltipLabel>Utilization Capacity</TooltipLabel>
            <TooltipValue>
              <TooltipHours value={value.capacity} />
            </TooltipValue>
          </TooltipInfo>

          <TooltipInfo>
            <TooltipLabel>Billable Hours</TooltipLabel>
            <TooltipValue>
              <TooltipHours value={value.billableHours} />
            </TooltipValue>
          </TooltipInfo>

          <TooltipInfo>
            <TooltipLabel>Billable Utilization</TooltipLabel>
            <TooltipValue>
              <Percent value={value.billableUtilization} />
            </TooltipValue>
          </TooltipInfo>

          <TooltipInfo>
            <TooltipLabel>Client Hours</TooltipLabel>
            <TooltipValue>
              <TooltipHours value={value.totalClientHours} />
            </TooltipValue>
          </TooltipInfo>

          <TooltipInfo>
            <TooltipLabel>Client Utilization</TooltipLabel>
            <TooltipValue>
              <Percent value={value.clientUtilization} />
            </TooltipValue>
          </TooltipInfo>

          <TooltipInfo>
            <TooltipLabel>Productive Hours</TooltipLabel>
            <TooltipValue>
              <TooltipHours value={value.totalProductiveHours} />
            </TooltipValue>
          </TooltipInfo>

          <TooltipInfo>
            <TooltipLabel>Productive Utilization</TooltipLabel>
            <TooltipValue>
              <Percent value={value.productiveUtilization} />
            </TooltipValue>
          </TooltipInfo>

          <TooltipInfo>
            <TooltipLabel>Time Off Hours</TooltipLabel>
            <TooltipValue>
              <TooltipHours value={value.timeOffHours} />
            </TooltipValue>
          </TooltipInfo>

          <TooltipInfo>
            <TooltipLabel>Total Hours</TooltipLabel>
            <TooltipValue>
              <TooltipHours value={value.totalHours} />
            </TooltipValue>
          </TooltipInfo>

          <TooltipInfo>
            <TooltipLabel>Total Utilization</TooltipLabel>
            <TooltipValue>
              <Percent value={value.totalUtilization} />
            </TooltipValue>
          </TooltipInfo>
        </TooltipContainer>
      }>
      <Percent
        value={
          {
            billable: value.billableUtilization,
            client: value.clientUtilization,
            productive: value.productiveUtilization,
            total: value.totalUtilization,
          }[metric || 'billable']
        }
      />
    </Tooltip>
  );
}

function MetricFilter({ value, onChange }) {
  const options = [
    { id: 'billable', name: 'Billable Utilization' },
    { id: 'client', name: 'Client Utilization' },
    { id: 'productive', name: 'Productive Utilization' },
    { id: 'total', name: 'Total Utilization' },
  ];

  return (
    <SingleSelectFilter
      icon="gear"
      placeholder="Metric"
      clearable={false}
      value={value}
      renderValue={(value) => value.name}
      options={options}
      onChange={onChange}
    />
  );
}

function UnitFilter(props) {
  return (
    <SingleSelectFilter
      icon="gear"
      placeholder="Unit"
      clearable={false}
      renderValue={(value) => value.name}
      options={[
        { id: 'month', name: 'Month' },
        { id: 'week', name: 'Week' },
        { id: 'day', name: 'Day' },
      ]}
      {...props}
    />
  );
}
